import { statusOf } from "../httpStatus";
import {
  acquireDownloadsLock,
  releaseDownloadsLock,
  renewDownloadsLock,
} from "./downloads.service";
import type { DownloadsLockConflict } from "./downloads.types";

export interface HeldLock {
  // Stops renewing and gives the turn back. Safe to call again; a release that
  // fails is ignored, because the lease expires by itself.
  release: () => void;
}

// The lock says when it expires, not how often to renew it. This is the upload
// hub's heartbeat, whose lease lasts 120 seconds.
const HEARTBEAT_MS = 30000;

// Takes the turn on these courses and keeps it while the caller works. A
// refusal or any other failure to take it rejects, with nothing left running.
export async function takeDownloadsLock(
  courseIds: number[],
  onLost: () => void,
): Promise<HeldLock> {
  const { lockId } = await acquireDownloadsLock(courseIds);
  let renewing = true;
  let released = false;

  const timer = setInterval(async () => {
    try {
      await renewDownloadsLock(lockId);
    } catch (error) {
      // Only a 409 says the lease is gone; any other failure may pass by the
      // next tick. A late answer after the release says nothing either.
      if (statusOf(error) === 409 && renewing) {
        renewing = false;
        clearInterval(timer);
        onLost();
      }
    }
  }, HEARTBEAT_MS);

  return {
    release: () => {
      if (released) return;
      released = true;
      renewing = false;
      clearInterval(timer);
      void (async () => {
        try {
          await releaseDownloadsLock(lockId);
        } catch {
          // The lease expires by itself.
        }
      })();
    },
  };
}

const clock = (iso: string): string => {
  const date = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const HELD = "Ya hay una descarga en curso con alguno de estos cursos";

// The 409 that says someone else holds some of these courses, worded for the
// operator; null for any other failure.
export function downloadsLockRefusal(error: unknown): string | null {
  if (statusOf(error) !== 409) return null;
  const conflict: DownloadsLockConflict =
    (error as { response?: { data?: DownloadsLockConflict } }).response?.data ??
    {};
  if (!conflict.holderName || !conflict.acquiredAt) return `${HELD}.`;
  return `${HELD}: la inició ${conflict.holderName} a las ${clock(conflict.acquiredAt)}.`;
}
